import { Component, Input, OnChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { ApplicationState } from 'src/app/state/reducers';
import { selectPaymentInfoByAccountNumber } from 'src/app/state/selectors/billing-payment-info.selectors';
import { filter, tap } from 'rxjs/operators';
import { OnDestroyHookDirective } from '../../common/hooks';
import { TranslationService } from '@dch/ng-common';
import {
  IPayButtonVisibility,
  isPayButtonVisible,
} from '../../services/billing-show-pay-button.service';
import { FormatPositiveAmountService } from '../../services/format-positive-amount.service';
import {
  IBillingAccount,
  IBillingDocument,
  IBillingTransactions,
  IPaymentInfo,
} from '@dch/billing-business-logic';

@Component({
  selector: 'my-bill-pay-button-container',
  templateUrl: './my-bill-pay-button-container.component.html',
  standalone: false,
})
export class MyBillPayButtonContainerComponent
  extends OnDestroyHookDirective
  implements OnChanges
{
  @Input() billingAccount: IBillingAccount;
  @Input() billingTransactions: IBillingTransactions;

  amount: string;
  billingDocuments: Array<IBillingDocument>;
  paymentInfo?: IPaymentInfo;
  payButtonVisibility: IPayButtonVisibility;
  isBalancePayButtonVisible = false;
  isBalanceOverduePayButtonVisible = false;
  isTransactionPayButtonVisible = false;
  balancePayButtonText: string;
  overduePayButtonText: string;
  transactionPayButtonText: string;
  openTransactionToPayIndex: number;

  constructor(
    private translationService: TranslationService,
    private formatPositiveAmountService: FormatPositiveAmountService,
    private store: Store<ApplicationState>
  ) {
    super();
  }
  ngOnChanges(): void {
    this.amount = this.billingAccount.balance;
    this.billingDocuments =
      this.billingTransactions.openTransactions.billingDocuments;
    this.setPaymentInfo();
    this.setPaybuttonVisibility();
  }

  private setPaymentInfo() {
    const paymentInfo$ = this.store.select(
      selectPaymentInfoByAccountNumber(this.billingAccount.accountNumber)
    );
    this.addSubscription(
      paymentInfo$
        .pipe(
          filter((paymentInfo) => {
            return paymentInfo && paymentInfo.result !== null;
          }),
          tap((paymentInfo) => {
            this.paymentInfo = paymentInfo.result;
          })
        )
        .subscribe()
    );
  }

  private setPaybuttonVisibility() {
    if (this.paymentInfo && this.billingTransactions && this.billingAccount) {
      const {
        isBalancePayButtonVisible,
        isBalanceOverduePayButtonVisible,
        isTransactionPayButtonVisible,
      } = isPayButtonVisible(
        this.paymentInfo,
        this.billingTransactions,
        this.billingAccount
      );
      this.isBalancePayButtonVisible = isBalancePayButtonVisible;
      this.isBalanceOverduePayButtonVisible = isBalanceOverduePayButtonVisible;
      this.isTransactionPayButtonVisible = isTransactionPayButtonVisible;
      this.setPayButtonText();
    }
  }

  private setPayButtonText() {
    const overdueBalance = this.billingAccount.overdueBalance;
    this.openTransactionToPayIndex = this.billingDocuments.findIndex(
      (document) => document.paymentInfo?.directDebitInfo === 'MANUAL'
    );
    const transactionBalance = this.getTransactionBalance();

    this.balancePayButtonText = this.getButtonText(
      'bills.redesign.payButton.payAll',
      this.amount
    );
    this.overduePayButtonText = this.getButtonText(
      'bills.redesign.payButton.payOverdue',
      overdueBalance
    );
    this.transactionPayButtonText = this.getButtonText(
      'bills.redesign.payButton.payManually',
      transactionBalance
    );
  }

  private getButtonText(translationKey: string, amount: string): string {
    return `${this.translationService.getTranslatedValueFor(
      translationKey
    )} (${this.formatPositiveAmountService.getFormattedPositiveAmountWithCurrencyAndDecimal(
      amount
    )})`;
  }

  private getTransactionBalance(): string {
    return this.isTransactionPayButtonVisible &&
      this.openTransactionToPayIndex !== -1
      ? this.billingDocuments[this.openTransactionToPayIndex].amount
      : '';
  }
}
